import React, { useEffect, useState, useCallback } from "react"
import Select from "react-select"
import axios from "axios"
import Swal from "sweetalert2"
import configure from "configure"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import { fetchNodeData } from "utils/common-fetchData"

// ── Tab definitions ────────────────────────────────────────────────────────────
const TAB_TYPES = [
  {
    key: "staff",
    label: "Staff",
    description: "Controls who can see the Staff tab in Overall Assessment View.",
    icon: "fas fa-user-tie",
    color: "#752a97",
    bg: "#f5eeff",
    border: "#d8b4fe",
  },
  {
    key: "internal_trainee",
    label: "Internal Trainee",
    description: "Controls who can see the Internal Trainee tab in Overall Assessment View.",
    icon: "fas fa-user-graduate",
    color: "#0369a1",
    bg: "#f0f9ff",
    border: "#bae6fd",
  },
  {
    key: "external_trainee",
    label: "External Trainee",
    description: "Controls who can see the External Trainee tab in Overall Assessment View — and the External Candidates dashboard element, which shares this same access list.",
    icon: "fas fa-user-clock",
    color: "#b45309",
    bg: "#fffbeb",
    border: "#fde68a",
  },
]

const AssessmentViewSettings = () => {
  const [loading, setLoading] = useState(false)
  const [staffOptions, setStaffOptions] = useState([])

  // State: { [tab_key]: { user_ids: string|null, id: number|null, dirty: bool } }
  // Restriction is always on — is_active:1 is sent on every save, no toggle needed.
  const [settings, setSettings] = useState(() =>
    Object.fromEntries(
      TAB_TYPES.map(t => [t.key, { user_ids: null, id: null, dirty: false }])
    )
  )

  const fetchStaff = useCallback(async () => {
    try {
      const token = secureLocalStorage.getItem("auth_token")
      const res = await fetchNodeData(null, "staff", token)
      if (res?.status) {
        const active = (res.data || []).filter(s => s.login_access == 1)
        setStaffOptions(active.map(u => ({ value: u.id, label: u.name })))
      }
    } catch (e) {
      console.warn("staff fetch error", e)
    }
  }, [])

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${configure.nodeApi}/preassesment/settings/tabAccess`, {
        headers: { Accept: "application/json" },
      })
      if (res.data.success) {
        const saved = res.data.data || []
        setSettings(prev => {
          const next = { ...prev }
          saved.forEach(row => {
            if (next[row.tab_key] !== undefined) {
              next[row.tab_key] = {
                ...next[row.tab_key],
                id: row.id,
                user_ids: row.user_ids || null,
                dirty: false,
              }
            }
          })
          return next
        })
      }
    } catch (e) {
      console.warn("tab access fetch error", e)
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchStaff(), fetchSettings()]).finally(() => setLoading(false))
  }, [fetchStaff, fetchSettings])

  const toSelectValue = user_ids => {
    if (!user_ids) return []
    return String(user_ids)
      .split(",")
      .map(s => s.trim())
      .filter(Boolean)
      .map(id => staffOptions.find(o => String(o.value) === id))
      .filter(Boolean)
  }

  const handleUsersChange = (tabKey, selected) => {
    const ids = (selected || []).map(o => o.value).join(",")
    setSettings(prev => ({
      ...prev,
      [tabKey]: { ...prev[tabKey], user_ids: ids || null, dirty: true },
    }))
  }

  const saveOne = async tabKey => {
    const row = settings[tabKey]
    const userId = localStorage.getItem("auth_id")
    return axios.put(
      `${configure.nodeApi}/preassesment/settings/tabAccess`,
      { tab_key: tabKey, user_ids: row.user_ids || null, is_active: 1, user_id: userId },
      { headers: { Accept: "application/json" } }
    )
  }

  const handleSave = async tabKey => {
    setLoading(true)
    try {
      const res = await saveOne(tabKey)
      if (res.data.success) {
        setSettings(prev => ({
          ...prev,
          [tabKey]: { ...prev[tabKey], id: res.data.data?.id || prev[tabKey].id, dirty: false },
        }))
        Swal.fire({ icon: "success", title: "Saved!", timer: 1200, showConfirmButton: false })
      } else {
        Swal.fire({ icon: "error", title: res.data.message || "Save failed" })
      }
    } catch (e) {
      Swal.fire({ icon: "error", title: "Something went wrong", text: e.message })
    } finally {
      setLoading(false)
    }
  }

  const handleSaveAll = async () => {
    setLoading(true)
    try {
      await Promise.all(TAB_TYPES.map(t => saveOne(t.key)))
      setSettings(prev => {
        const next = { ...prev }
        TAB_TYPES.forEach(t => { next[t.key] = { ...next[t.key], dirty: false } })
        return next
      })
      Swal.fire({ icon: "success", title: "All tab access settings saved!", timer: 1500, showConfirmButton: false })
    } catch (e) {
      Swal.fire({ icon: "error", title: "Something went wrong", text: e.message })
    } finally {
      setLoading(false)
    }
  }

  const dirtyCount = TAB_TYPES.filter(t => settings[t.key]?.dirty).length

  return (
    <>
      {loading && <Loader />}
      <div className="p-4 bg-white shadow">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-3">
          <div>
            <h5 style={{ fontWeight: 700, color: "#1f2937", fontSize: 17, marginBottom: 4 }}>
              Overall Assessment View — Tab Access
            </h5>
            <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
              Choose which staff can see each tab (Staff / Internal Trainee / External Trainee).
            </p>
          </div>
          {dirtyCount > 0 && (
            <button className="btn btn-save btn-label" onClick={handleSaveAll}>
              <i className="fas fa-save me-2 label-icon"></i>
              Save All ({dirtyCount} changed)
            </button>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {TAB_TYPES.map(tab => {
            const row = settings[tab.key]
            const selectedUsers = toSelectValue(row.user_ids)

            return (
              <div
                key={tab.key}
                style={{
                  border: `1.5px solid ${row.dirty ? "#c89ee0" : tab.border}`,
                  borderLeft: `4px solid ${tab.color}`,
                  borderRadius: 10,
                  padding: "18px 20px",
                  background: tab.bg,
                  transition: "border-color .15s",
                }}
              >
                <div className="d-flex align-items-start gap-3 flex-wrap">
                  <div style={{
                    width: 42, height: 42, borderRadius: 10,
                    background: tab.color,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <i className={`${tab.icon} text-white`} style={{ fontSize: 17 }}></i>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                      <span style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>{tab.label}</span>
                      {row.dirty && (
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "#fef3c7", color: "#92400e" }}>
                          Unsaved
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 14, lineHeight: 1.5 }}>
                      {tab.description}
                    </p>

                    <div style={{ maxWidth: 560 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#4b5563", marginBottom: 5, display: "block" }}>
                        <i className="fas fa-users me-1" style={{ color: tab.color }}></i>
                        Allowed Staff
                      </label>
                      <Select
                        isMulti
                        placeholder="Select staff who can view this tab…"
                        options={staffOptions}
                        value={selectedUsers}
                        onChange={val => handleUsersChange(tab.key, val)}
                        classNamePrefix="select"
                        styles={{
                          control: base => ({ ...base, fontSize: 13, borderColor: "#e5e7eb", minHeight: 36 }),
                          menu: base => ({ ...base, fontSize: 13, zIndex: 9999 }),
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <button className="btn btn-sm btn-save btn-label" onClick={() => handleSave(tab.key)}>
                      <i className="fas fa-save me-1 label-icon"></i>
                      Save
                    </button>
                  </div>
                </div>

                {selectedUsers.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-3" style={{ paddingLeft: 58 }}>
                    {selectedUsers.map(u => (
                      <span key={u.value} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "#fff", border: `1px solid ${tab.border}`, color: tab.color, fontWeight: 500 }}>
                        <i className="fas fa-user me-1" style={{ fontSize: 9 }}></i>
                        {u.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="d-flex align-items-center gap-3 mt-4 pt-3 flex-wrap" style={{ borderTop: "1px solid #f3f4f6", fontSize: 12, color: "#9ca3af" }}>
          <span>
            <i className="fas fa-info-circle me-1"></i>
            Changes to individual rows are saved immediately via the row-level
            <strong className="ms-1">Save</strong> button, or use
            <strong className="ms-1">Save All</strong> when multiple rows are changed.
          </span>
        </div>
      </div>
    </>
  )
}

export default AssessmentViewSettings
